import React from "react";
import { DataCell } from "@/components/DataCell";
import { PercentChange } from "@/components/DataCell/PercentChange";
import { useTranslation } from "react-i18next";
import { Users, TrendingUp, School, UsersRound } from "lucide-react";
import { useContextMetrics } from "@/hooks/useContextMetrics";
import { useCurrentCohortYear } from "@/hooks/useCurrentCohortYear";
import { OrganizationMetricsContextEnum } from "@/__generated__/types";
import { CircleIcon } from "./CircleIcon";

interface DashboardOverviewDataCellsProps {
  orgId?: string;
  orgIdLoading?: boolean;
  orgIdError?: unknown;
}

export const DashboardOverviewDataCells: React.FC<
  DashboardOverviewDataCellsProps
> = ({ orgId, orgIdLoading = false, orgIdError }) => {
  const { t } = useTranslation();

  const {
    loading: metricsLoading,
    error: metricsError,
    hasPreviousMetricValue,
    getPreviousCycleValue,
    getMetricDisplayValue,
    getMetricCalculatedPercentChanged,
  } = useContextMetrics(
    orgId,
    OrganizationMetricsContextEnum.CommunitySnapshot,
  );

  const { cohortYear } = useCurrentCohortYear();

  const hasRequiredData = Boolean(orgId);

  const isLoading =
    !orgIdError && !metricsError && (orgIdLoading || metricsLoading);
  const hasError =
    orgIdError || metricsError || (!hasRequiredData && !orgIdLoading);

  const errorContent = (
    <span className="text-sm text-muted-foreground">
      {t("home.overview.dataCells.error.loadingError")}
    </span>
  );

  return (
    <div>
      {/* Title Section */}
      <h2 className="mb-2 text-sm leading-5 font-normal text-muted-foreground">
        {t("home.overview.dataCells.title", { cohort: cohortYear })}
      </h2>

      {/* Data Cells Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {/* Community Members */}
        <DataCell
          className="min-h-[142px]"
          isLoading={isLoading}
          title={t("home.overview.dataCells.communityMembers")}
          tooltipText={t("home.overview.dataCells.communityMembersToolTip")}
          middleValue={
            hasError ? errorContent : getMetricDisplayValue("communityMembers")
          }
          bottomValue={
            hasError ? null : (
              <PercentChange
                value={getMetricCalculatedPercentChanged("communityMembers")}
                prevCount={getPreviousCycleValue("communityMembers")}
                customTemplate={t("metrics.comparisons.yearOverYear", {
                  percentChanged: "{{percentChange}}",
                })}
              />
            )
          }
          icon={
            <CircleIcon color="indigo">
              <Users />
            </CircleIcon>
          }
        />
        {/* High Index Students */}
        <DataCell
          className="min-h-[142px]"
          isLoading={isLoading}
          title={t("home.overview.dataCells.highIndexStudents")}
          tooltipText={t("home.overview.dataCells.highIndexStudentsToolTip")}
          middleValue={
            hasError ? errorContent : getMetricDisplayValue("highIndexStudents")
          }
          bottomValue={
            hasError ? null : (
              <PercentChange
                value={getMetricCalculatedPercentChanged("highIndexStudents")}
                prevCount={getPreviousCycleValue("highIndexStudents")}
                customTemplate={t("metrics.comparisons.yearOverYear", {
                  percentChanged: "{{percentChange}}",
                })}
              />
            )
          }
          icon={
            <CircleIcon color="yellow">
              <TrendingUp />
            </CircleIcon>
          }
        />
        {/* In Community Commits */}
        <DataCell
          className="min-h-[142px]"
          isLoading={isLoading}
          title={t("home.overview.dataCells.inCommunityCommits")}
          tooltipText={t("home.overview.dataCells.inCommunityCommitsToolTip")}
          middleValue={
            hasError
              ? errorContent
              : getMetricDisplayValue("inCommunityCommits")
          }
          bottomValue={
            hasError ? null : (
              <PercentChange
                value={getMetricCalculatedPercentChanged("inCommunityCommits")}
                prevCount={getPreviousCycleValue("inCommunityCommits")}
                customTemplate={t("metrics.comparisons.yearOverYear", {
                  percentChanged: "{{percentChange}}",
                })}
              />
            )
          }
          icon={
            <CircleIcon color="lime">
              <UsersRound />
            </CircleIcon>
          }
        />
        {/* In Community Yield Rate */}
        <DataCell
          className="min-h-[142px]"
          isLoading={isLoading}
          title={t("home.overview.dataCells.inCommunityYieldRate")}
          tooltipText={t("home.overview.dataCells.inCommunityYieldRateToolTip")}
          middleValue={
            hasError
              ? errorContent
              : getMetricDisplayValue("inCommunityYieldRate", true)
          }
          bottomValue={
            hasError ? null : (
              <span className="text-sm text-muted-foreground">
                {hasPreviousMetricValue("inCommunityYieldRate")
                  ? t("metrics.comparisons.vsLastCyclePercent", {
                      percentChanged: Math.round(
                        getPreviousCycleValue("inCommunityYieldRate") || 0,
                      ),
                    })
                  : t("metrics.comparisons.noDataLastCycle")}
              </span>
            )
          }
          icon={
            <CircleIcon color="orange">
              <School />
            </CircleIcon>
          }
        />
      </div>
    </div>
  );
};
